import { type AsyncWsRequest, useAsyncWsRequest } from '@/hooks/use-async-ws-request';
import { type FileDownloadPush, isFileDownloadPush, useFileDownloadListener } from '@/lib/websocket';

// The server jobs behind these can run up to a Lambda's 10 minutes; there is no failure push, so the
// client stops waiting itself. Callers with a spec-driven shorter grace period pass timeoutMs.
const DEFAULT_TIMEOUT_MS = 10 * 60_000;

interface UseAsyncFileDownloadOptions<TArg = void> {
  /** Starts the server-side job, receiving the value passed to the returned `start`. */
  request: (arg: TArg) => Promise<unknown>;
  /** Names the job in the wait/error toasts, e.g. "export", "GDPR report". */
  label: string;
  timeoutMs?: number;
}

/**
 * Request-then-download over the shared websocket, the fileDownload sibling of useAsyncReport: POSTs the
 * job and waits for the {type:'fileDownload'} push, which the shared listener turns into the browser
 * download + success toast. Callers wrap this with their endpoint and their own naming.
 */
export const useAsyncFileDownload = <TArg = void>({ request, label, timeoutMs }: UseAsyncFileDownloadOptions<TArg>): AsyncWsRequest<TArg> => {
  useFileDownloadListener();

  return useAsyncWsRequest<FileDownloadPush, TArg>({
    request,
    isMatch: isFileDownloadPush,
    timeoutMs: timeoutMs ?? DEFAULT_TIMEOUT_MS,
    timeoutMessage: `The ${label} is still being generated — it will download when ready`,
    requestErrorMessage: `Failed to request the ${label}`,
  });
};
